import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { CalendarView } from 'angular-calendar';
import moment from 'moment';
import 'moment/locale/es';
import 'moment/locale/ca';
import { SessionService } from '../../../../_services/session/session.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { CommonModule } from '@angular/common';
import { SelectorCalendarModeComponent } from '../../../selectors/selector-calendar-mode/selector-calendar-mode.component';
import { BaseComponent } from '../../../base/base.component';
import ICalendar from '../../../../_interfaces/ICalendar';
import { SelectorCalendarPeriodComponent } from '../../../selectors/selector-calendar-period/selector-calendar-period.component';
import { CalendarAppointmentsActionsButtonsComponent } from './calendar-appointments-actions-buttons/calendar-appointments-actions-buttons.component';
import { PopoverModule } from 'primeng/popover';
import { DatePickerModule } from 'primeng/datepicker';
import { FormsModule } from '@angular/forms';
import { DialogService } from 'primeng/dynamicdialog';
import { FormCreateAppointmentComponent } from '../../../forms/form-create-appointment/form-create-appointment.component';
import { FormCalendarNoteComponent } from '../../../forms/form-calendar-note/form-calendar-note.component';
import { AppointmentService } from '../../../../_services/appointments/appointment.service';
import IAppointment from '../../../../_interfaces/IAppointment';
import { RejectAppointmentRequestDialogComponent } from '../../../appointment/reject-appointment-request-dialog/reject-appointment-request-dialog.component';
import { IAppointmentRejectRequestPayload } from '../../../../_interfaces/IAppointmentRequest';

@Component({
  selector: 'app-calendar-appointments-actions',
  imports: [
    ButtonModule,
    TooltipModule,
    CommonModule,
    TranslatePipe,
    SelectorCalendarModeComponent,
    SelectorCalendarPeriodComponent,
    CalendarAppointmentsActionsButtonsComponent,
    PopoverModule,
    DatePickerModule,
    FormsModule,
    RejectAppointmentRequestDialogComponent,
  ],
  templateUrl: './calendar-appointments-actions.component.html',
  styleUrl: './calendar-appointments-actions.component.scss',
})
export class CalendarAppointmentsActionsComponent
  extends BaseComponent
  implements OnInit
{
  @ViewChild('pendingRequestsPopover') pendingRequestsPopover: any;
  @Input('view') view: CalendarView;

  @Input('quickMode')
  public quickMode: boolean = false;
  @Input('calendar') calendars: ICalendar[];

  @Input('openSettings')
  public openSettings: (() => void) | undefined;

  @Input('onAction') onAction: (config: {
    date?: Date;
    view?: CalendarView;
    retrieve: {
      appointments: boolean;
      notes: boolean;
    };
  }) => void;

  @Input('viewDate')
  public viewDate: Date = moment().toDate();

  /**
   * Retorna el objeto de vista seleccionada para el selector
   */
  get selectedView() {
    return { code: this.view, name: this.view };
  }

  // Mobile properties
  public selectedMobileDate: Date = new Date();
  public pendingRequestsCount = 0;
  public pendingRequestsLoading = false;
  public pendingRequests: IAppointment[] = [];
  public rejectDialogVisible = false;
  public rejectRequestWorking = false;
  public rejectTarget: IAppointment | null = null;

  /**
   * Título compacto para móvil
   */
  get mobileTitle(): string {
    moment.locale(this.translate.currentLang || 'es');

    if (this.view === CalendarView.Month) {
      return moment(this.viewDate).format('MMM YYYY');
    }
    if (this.view === CalendarView.Day) {
      return moment(this.viewDate).format('ddd DD MMM');
    }
    // Week view
    return (
      moment(this.viewDate).format('DD MMM') +
      ' - S' +
      moment(this.viewDate).format('WW')
    );
  }

  /**
   * Verificar si es hoy
   */
  get isToday(): boolean {
    return moment(this.viewDate).isSame(moment(), 'day');
  }

  /**
   * Verificar vista activa
   */
  get isMonthView(): boolean {
    return this.view === CalendarView.Month;
  }

  get isWeekView(): boolean {
    return this.view === CalendarView.Week;
  }

  get isDayView(): boolean {
    return this.view === CalendarView.Day;
  }

  constructor(
    public session: SessionService,
    private translate: TranslateService,
    private dialogService: DialogService,
    private appointmentService: AppointmentService,
  ) {
    super(session);
  }

  private isExpiredPortalPendingRequest(
    appointment?: IAppointment | null,
    now: Date = new Date(),
  ): boolean {
    if (!appointment) return false;
    if (appointment.source !== 'PATIENT_PORTAL') return false;
    if (appointment.approval_state !== 'PENDING_APPROVAL') return false;
    if (!appointment.end) return false;
    return new Date(appointment.end).getTime() < now.getTime();
  }

  private filterVisiblePendingRequests(
    appointments: IAppointment[] | null | undefined,
  ): IAppointment[] {
    const now = new Date();
    return (appointments || []).filter(
      (appointment) => !this.isExpiredPortalPendingRequest(appointment, now),
    );
  }

  public onModeChange = (view: CalendarView) => {
    this.view = view;
    this.onAction({
      date: this.viewDate,
      view: this.view,
      retrieve: { appointments: true, notes: true },
    });
  };

  /**
   * Cambiar vista desde móvil
   */
  public setView(viewType: 'month' | 'week' | 'day'): void {
    const viewMap = {
      month: CalendarView.Month,
      week: CalendarView.Week,
      day: CalendarView.Day,
    };
    this.onModeChange(viewMap[viewType]);
  }

  /**
   * Ir a hoy
   */
  public goToToday(): void {
    this.onAction({
      date: moment().toDate(),
      retrieve: { appointments: true, notes: true },
    });
  }

  /**
   * Navegar al período anterior
   */
  public navigatePrevious(): void {
    const amount = this.getNavigationAmount();
    const unit = this.getNavigationUnit();
    const newDate = moment(this.viewDate).subtract(amount, unit).toDate();
    this.onAction({
      date: newDate,
      retrieve: { appointments: true, notes: true },
    });
  }

  /**
   * Navegar al período siguiente
   */
  public navigateNext(): void {
    const amount = this.getNavigationAmount();
    const unit = this.getNavigationUnit();
    const newDate = moment(this.viewDate).add(amount, unit).toDate();
    this.onAction({
      date: newDate,
      retrieve: { appointments: true, notes: true },
    });
  }

  /**
   * Obtener la unidad de navegación según la vista
   */
  private getNavigationUnit(): moment.unitOfTime.DurationConstructor {
    if (this.view === CalendarView.Month) return 'months';
    if (this.view === CalendarView.Day) return 'days';
    return 'days'; // Week view
  }

  /**
   * Obtener la cantidad de navegación según la vista
   * En móvil, la semana muestra 3 días, así que navegamos de 3 en 3
   */
  private getNavigationAmount(): number {
    if (this.view === CalendarView.Month) return 1;
    if (this.view === CalendarView.Day) return 1;
    // Week view: 3 días en móvil, 7 en desktop
    return this.isMobile() ? 3 : 7;
  }

  /**
   * Detectar si estamos en móvil
   */
  private isMobile(): boolean {
    return window.innerWidth <= 768;
  }

  /**
   * Seleccionar fecha desde móvil
   */
  public onMobileDateSelect(date: Date): void {
    this.onAction({
      date: date,
      retrieve: { appointments: true, notes: true },
    });
  }

  /**
   * Añadir cita desde móvil
   */
  public addAppointmentMobile(): void {
    this.dialogService.open(FormCreateAppointmentComponent, {
      width: '92vw',
      contentStyle: {
        height: 'min(82vh, 56rem)',
        'max-height': '82vh',
        overflow: 'hidden',
      },
      closable: true,
      modal: true,
      data: {
        startDate: moment(this.viewDate).format('DD-MM-YYYY HH:mm'),
        calendar: this.calendars,
        callback: () => {
          this.onAction({
            retrieve: { appointments: true, notes: false },
          });
        },
      },
      header: this.translate.instant('APPOINTMENT.ADD'),
    });
  }

  /**
   * Añadir nota desde móvil
   */
  public addNoteMobile(): void {
    const ref = this.dialogService.open(FormCalendarNoteComponent, {
      width: '95%',
      closable: true,
      modal: true,
      data: {
        calendars: this.calendars,
        date: this.viewDate,
      },
      inputValues: {
        onUpdate: () => {
          ref.close();
          this.onAction({
            retrieve: { appointments: false, notes: true },
          });
        },
      },
      header: this.translate.instant('CALENDAR.NOTES.ADD'),
    });
  }

  public loadPendingRequests() {
    if (!this.customers_id || !this.site?.id) {
      this.pendingRequests = [];
      this.pendingRequestsCount = 0;
      return;
    }

    this.pendingRequestsLoading = true;
    this.appointmentService
      .getPatientRequests(this.customers_id, this.site, 'pending')
      .subscribe({
        next: (requests) => {
          this.pendingRequestsLoading = false;
          this.pendingRequests = this.filterVisiblePendingRequests(requests);
          this.pendingRequestsCount = this.pendingRequests.length;
        },
        error: () => {
          this.pendingRequestsLoading = false;
          this.pendingRequests = [];
          this.pendingRequestsCount = 0;
        },
      });
  }

  public loadPendingRequestsCount() {
    if (!this.customers_id || !this.site?.id) {
      this.pendingRequestsCount = 0;
      return;
    }

    this.appointmentService
      .getPatientRequestsCount(this.customers_id, this.site)
      .subscribe({
        next: (response) => {
          this.pendingRequestsCount = Number(response?.pending || 0);
        },
        error: () => {
          this.pendingRequestsCount = 0;
        },
      });
  }

  public approveRequest(appointment: IAppointment) {
    if (!appointment?.id || !this.customers_id || !this.site?.id) return;
    this.appointmentService
      .approvePatientRequest(this.customers_id, this.site, appointment.id)
      .subscribe({
        next: () => {
          this.loadPendingRequests();
          this.onAction({
            retrieve: { appointments: true, notes: false },
          });
        },
      });
  }

  public rejectRequest(appointment: IAppointment) {
    if (!appointment?.id) return;

    this.rejectTarget = appointment;
    this.pendingRequestsPopover?.hide?.();
    this.rejectDialogVisible = true;
  }

  public confirmRejectRequest(payload: IAppointmentRejectRequestPayload) {
    if (
      !this.rejectTarget?.id ||
      !this.customers_id ||
      !this.site?.id ||
      this.rejectRequestWorking
    ) {
      return;
    }

    this.rejectRequestWorking = true;
    this.appointmentService
      .rejectPatientRequest(this.customers_id, this.site, this.rejectTarget.id, {
        reason: payload?.reason,
        counter_proposal: payload?.counter_proposal,
      })
      .subscribe({
        next: () => {
          this.rejectRequestWorking = false;
          this.closeRejectDialog();
          this.loadPendingRequests();
          this.onAction({
            retrieve: { appointments: true, notes: false },
          });
        },
        error: () => {
          this.rejectRequestWorking = false;
        },
      });
  }

  public closeRejectDialog() {
    this.rejectDialogVisible = false;
    this.rejectTarget = null;
  }

  public toggleRequestsPopover(event: Event) {
    this.loadPendingRequests();
    this.pendingRequestsPopover?.toggle(event);
  }

  public getRequestTitle(appointment: IAppointment): string {
    const patient = appointment?.patient;
    if (patient?.name || patient?.surname) {
      return [patient.name, patient.surname].filter(Boolean).join(' ');
    }
    return this.translate.instant('APPOINTMENT.PATIENT.TITLE');
  }

  public getRequestTreatmentNames(appointment: IAppointment): string {
    return (appointment?.treatments || [])
      .map((row) => row?.treatment?.name)
      .filter(Boolean)
      .join(', ');
  }

  public ngOnInit(): void {
    super.ngOnInit();
    this.selectedMobileDate = this.viewDate;
    this.loadPendingRequestsCount();
  }
}
